import type { MeResponse, ResearchRecord } from "@pokifolio/contracts";
import { Link } from "@tanstack/react-router";
import { Archive, Eye, MoreHorizontal, Trash2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { closeRule, deleteRule } from "./manage-rules";

interface ResearchRowActionsProps {
  readonly record: ResearchRecord;
  readonly me: MeResponse;
  readonly onClose: (record: ResearchRecord) => void;
  readonly onDelete: (record: ResearchRecord) => void;
}

/** The "…" menu at the end of a Research row: open, close, delete (each only when allowed). */
export function ResearchRowActions({ record, me, onClose, onDelete }: ResearchRowActionsProps) {
  const close = closeRule(record, me);
  const remove = deleteRule(record, me);
  const name = `${record.card.name} ${record.card.number}`;
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-8 data-[state=open]:bg-muted"
          aria-label={`Actions for ${name}`}
        >
          <MoreHorizontal aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem asChild>
          <Link to="/research/$researchId" params={{ researchId: record.id }}>
            <Eye aria-hidden="true" />
            Open
          </Link>
        </DropdownMenuItem>
        {close.allowed && (
          <DropdownMenuItem onSelect={() => onClose(record)}>
            <Archive aria-hidden="true" />
            Close…
          </DropdownMenuItem>
        )}
        {remove.allowed && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={() => onDelete(record)}>
              <Trash2 aria-hidden="true" />
              Delete…
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
